import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send } from "lucide-react";
import api from "@/lib/api";
import { TID } from "@/constants/testIds";

export default function ConciergeWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "G'day! I'm your Gold Coast concierge. Where are you headed — Burleigh, Broadbeach, hinterland? Ask me anything: dinner tonight, hidden beaches, family day out." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || busy) return;
    const msg = input.trim();
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/concierge/chat", { session_id: sessionId, message: msg });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "The concierge is briefly unavailable — try again in a moment." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={TID.conciergeFab}
        className="fixed bottom-6 right-6 z-50 rounded-full bg-[color:var(--forest)] text-[color:var(--bone)] px-5 py-3 flex items-center gap-2 shadow-[0_16px_40px_-12px_rgba(11,37,36,0.6)] hover:bg-[color:var(--terracotta)] transition-colors"
        aria-label="Open concierge"
      >
        <Sparkles size={16} />
        <span className="font-medium text-sm">Ask the concierge</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 z-50 w-[min(92vw,420px)] h-[min(70vh,600px)] rounded-3xl overflow-hidden shadow-[0_40px_80px_-20px_rgba(11,37,36,0.4)] glass-card flex flex-col"
          >
            <div className="px-5 py-4 border-b border-[color:var(--forest-line)] flex items-center justify-between">
              <div>
                <div className="text-[0.7rem] uppercase tracking-[0.22em] text-[color:var(--forest-2)]">Hey GC</div>
                <div className="font-display text-xl leading-tight">The Concierge</div>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close" className="p-2 rounded-full hover:bg-black/5">
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
              {messages.map((m, i) => (
                <div key={i} data-testid={TID.conciergeMessage(i)} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-4 py-3 text-[0.92rem] leading-relaxed ${m.role === "user" ? "bubble-user" : "bubble-assistant"}`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {busy && (
                <div className="flex justify-start">
                  <div className="bubble-assistant px-4 py-3 text-[0.92rem]">
                    <span className="inline-flex gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60 animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60 animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60 animate-bounce" style={{ animationDelay: "300ms" }} />
                    </span>
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>
            <form onSubmit={send} className="border-t border-[color:var(--forest-line)] p-3 flex gap-2 bg-white/60">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Best sunset spot for Friday?"
                data-testid={TID.conciergeInput}
                className="flex-1 bg-white/80 border border-[color:var(--forest-line)] rounded-full px-4 py-2.5 text-sm focus:border-[color:var(--terracotta)] outline-none"
              />
              <button type="submit" disabled={busy} data-testid={TID.conciergeSend} className="btn-pill btn-primary px-4 disabled:opacity-40">
                <Send size={15} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
